import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_DATA, SITE_URL } from '../data/Constants';

/**
 * Schema component for injecting JSON-LD structured data.
 */
const Schema = ({ type, data }) => {
   const getBaseSchema = () => ({
      "@context": "https://schema.org",
      "@type": type,
      ...data
   });

   const getLocalBusinessSchema = () => ({
      "@context": "https://schema.org",
      "@type": "HomeGoodsStore",
      "name": SITE_DATA.brandName,
      "image": `${SITE_URL}${SITE_DATA.brandLogo}`,
      "@id": `${SITE_URL}/#organization`,
      "url": SITE_URL,
      "telephone": SITE_DATA.phone,
      "address": {
         "@type": "PostalAddress",
         "streetAddress": "201 - 20771 Langley Bypass",
         "addressLocality": "Langley",
         "addressRegion": "BC",
         "postalCode": "V3A 5E8",
         "addressCountry": "CA"
      },
      "geo": {
         "@type": "GeoCoordinates",
         "latitude": 49.113411,
         "longitude": -122.649938
      },
      "openingHoursSpecification": [
         {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "16:30"
         },
         {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": "Saturday",
            "opens": "10:00",
            "closes": "16:00"
         }
      ],
      "sameAs": [
         "https://www.facebook.com/mainlandfireplaces/",
         "https://www.instagram.com/mainlandfireplaces/"
      ]
   });

   const getBreadcrumbSchema = (items) => ({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items.map((item, index) => ({
         "@type": "ListItem",
         "position": index + 1,
         "name": item.name,
         "item": `${SITE_URL}${item.path}`
      }))
   });

   const getReviewSchema = (reviews) => ({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": SITE_DATA.brandName,
      "aggregateRating": {
         "@type": "AggregateRating",
         "ratingValue": "5",
         "reviewCount": String(reviews.length),
         "bestRating": "5",
         "worstRating": "1"
      },
      "review": reviews.map(review => ({
         "@type": "Review",
         "author": {
            "@type": "Person",
            "name": review.name
         },
         "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
         },
         "reviewBody": review.text
      }))
   });

   const getFAQSchema = (questions) => ({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": questions.map(q => ({
         "@type": "Question",
         "name": q.question,
         "acceptedAnswer": {
            "@type": "Answer",
            "text": q.answer
         }
      }))
   });

   const getProductSchema = (product) => {
      if (!product) return null;
      
      const cleanDescription = product.description ? String(product.description).replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() : '';
      
      const additionalProperty = [];
      const addProp = (name, value) => {
         if (value === undefined || value === null) return;
         if (Array.isArray(value)) {
            value.forEach(v => additionalProperty.push({ "@type": "PropertyValue", "name": name, "value": String(v).replace(/^['"]|['"]$/g, '') }));
         } else {
            String(value).split(',').forEach(v => additionalProperty.push({ "@type": "PropertyValue", "name": name, "value": v.trim().replace(/^['"]|['"]$/g, '') }));
         }
      };

      addProp('Sizes', product.sizes || product.size);
      addProp('Color', product.colors || product.color);
      addProp('Ignition', product.ignition || product.ignitions);
      
      const power = product['power voltage'] || product.power_voltage;
      if (power) {
         if (typeof power === 'object' && !Array.isArray(power)) {
             Object.keys(power).forEach(watts => {
                 additionalProperty.push({ "@type": "PropertyValue", "name": "Power Output", "value": `${watts}W` });
                 const volts = power[watts];
                 if (volts) {
                     String(volts).split(',').forEach(v => {
                         additionalProperty.push({ "@type": "PropertyValue", "name": "Voltage", "value": `${v.trim().replace(/^['"]|['"]$/g, '')}V` });
                     });
                 }
             });
         } else {
             addProp('Voltage', power);
         }
      }

      const subjectOf = [];
      const addManual = (key, name) => {
          const urls = product[key];
          if (!urls) return;
          const urlList = Array.isArray(urls) ? urls : [urls];
          urlList.forEach(url => {
              if(url && typeof url === 'string') {
                 subjectOf.push({
                    "@type": "DigitalDocument",
                    "name": name,
                    "url": url
                 });
              }
          });
      };
      
      addManual('spec sheet', 'Specification Sheet');
      addManual('user manual', 'User Manual');
      addManual('installation manual', 'Installation Manual');
      addManual('brochure', 'Brochure');
      addManual('CAD drawing', 'CAD Drawing');

      const schema = {
         "@context": "https://schema.org",
         "@type": "Product",
         "name": product.name,
         "image": product.images || [],
         "description": cleanDescription,
         "sku": product.sku,
         "brand": {
            "@type": "Brand",
            "name": product.company || "Mainland Fireplaces"
         }
      };

      if (additionalProperty.length > 0) schema.additionalProperty = additionalProperty;
      if (subjectOf.length > 0) schema.subjectOf = subjectOf;

      return schema;
   };

   const getCollectionPageSchema = (colData) => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": colData.name,
      "description": colData.description,
      "url": colData.url
   });

   const getItemListSchema = (items) => {
      if (!items || !items.length) return null;
      return {
         "@context": "https://schema.org",
         "@type": "ItemList",
         "itemListElement": items.map((item, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "url": item.url
         }))
      };
   };

   let schemaData = null;

   if (type === 'LocalBusiness') {
      schemaData = getLocalBusinessSchema();
   } else if (type === 'BreadcrumbList') {
      schemaData = getBreadcrumbSchema(data.items);
   } else if (type === 'AggregateRating') {
      schemaData = getReviewSchema(data.reviews);
   } else if (type === 'FAQPage') {
      schemaData = getFAQSchema(data.questions);
   } else if (type === 'Product' && data && data.sku) {
      schemaData = getProductSchema(data);
   } else if (type === 'CollectionPage' && data) {
      schemaData = getCollectionPageSchema(data);
   } else if (type === 'ItemList' && data) {
      schemaData = getItemListSchema(data.items);
   } else {
      schemaData = getBaseSchema();
   }

   if (!schemaData) return null;

   return (
      <Helmet>
         <script type="application/ld+json">
            {JSON.stringify(schemaData)}
         </script>
      </Helmet>
   );
};

export default Schema;
